import { Select, Stack, Text, TextInput, Textarea, Box, Paper, Alert, MultiSelect } from '@mantine/core';
import { SectionProps } from '../../types';
import classes from './Basic.module.css';
import { WidgetType } from '../../../types';
import { useState, useEffect } from 'react';
import { IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  widgetDescriptionFallback, widgetDescriptionKey, widgetLabelFallback, widgetLabelKey
} from '../../../../widgetCatalog';

type BasicSectionProps = SectionProps & {
  availableCubes: { value: string; label: string }[];
  /** True while the live `/meta` catalog is being resolved. */
  cubeMetaLoading?: boolean;
  /** Non-null when `/meta` is unreachable — the Data Source select shows an error and save is disabled. */
  cubeMetaError?: string | null;
  selectedCube: string;
  setSelectedCube: ( value: string ) => void;
  availableWidgetTypes: WidgetType[];
  widgetType: WidgetType;
  setWidgetType: ( value: WidgetType ) => void;
};

export default function BasicSection({ availableCubes, cubeMetaLoading, cubeMetaError, selectedCube, setSelectedCube, availableWidgetTypes, widgetType: propWidgetType, setWidgetType, config, updateConfig, validationErrors, getAvailableMeasures }: BasicSectionProps ) {
  const { t } = useTranslation( 'widgets' );
  const [ localWidgetType, setLocalWidgetType ] = useState<WidgetType>( propWidgetType );
  useEffect(() => {
    setLocalWidgetType( propWidgetType );
  }, [propWidgetType]);

  const handleWidgetTypeChange = ( value: string ) => {
    const newWidgetType = value as WidgetType;
    setLocalWidgetType( newWidgetType );
    setWidgetType( newWidgetType );
  };

  const sectionErrors = validationErrors?.basic;

  const availableMeasureOptions = getAvailableMeasures();

  return (
    <div className={classes.container}>
      <Stack gap="md">
        {/*
          TRIMMED 2026-08-17. What used to sit here:

          - an `<h4>Configuración básica` immediately under an accordion header
            already reading "Básico" — the same word twice, costing a line each;
          - a permanent blue "Configure the data source and widget type. These
            settings are required…" notice. It was instructions for assembling a
            card from nothing, which is no longer how a card starts: the palette
            hands over a configured one (`widgetPresets.ts`). A standing banner
            explaining a problem the user no longer has is just less room for the
            fields.

          The error block below stays — it is CONDITIONAL, and it only appears once
          something is genuinely wrong.
        */}
        {sectionErrors && Object.keys( sectionErrors ).length > 0 && (
          <Alert icon={<IconAlertCircle size="1.2rem" />} color="red" variant="light" mb="xs">
            <Text size='sm' c="red.9">
              {t( 'widget_builder_basic_configuration_errors_message','Please fix the following errors:' )}
              <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                {Object.entries( sectionErrors ).map(([ field, error ]) => (
                  <li key={field}>{error}</li>
                ))}
              </ul>
            </Text>
          </Alert>
        )}

        <Box className={classes.content}>
          {/* Data Source Selection */}
          <Paper p="md" className={classes.importantContent} bg={'primarylight.0'}>
            <Select
              label={t(
                'widget_builder_basic_configuration_data_source_label',
                'Data Source'
              )}
              placeholder={
                cubeMetaLoading
                  ? t( 'widget_builder_basic_configuration_data_source_loading', 'Loading data sources…' )
                  : t( 'widget_builder_basic_configuration_data_source_placeholder', 'Select a data source' )
              }
              data={availableCubes}
              value={selectedCube}
              onChange={( value ) => setSelectedCube( value || '' )}
              clearable
              disabled={!!cubeMetaError || cubeMetaLoading}
              className={classes.select}
              checkIconPosition="right"
              error={
                cubeMetaError
                  ? t( 'widget_builder_basic_configuration_data_source_error', 'Could not load data sources. Please refresh and try again.' )
                  : sectionErrors?.cube
              }
              nothingFoundMessage={t( 'widget_builder_basic_configuration_data_source_empty', 'No data sources available' )}
            />

            {selectedCube && (
              <MultiSelect
                label={t(
                  'widget_builder_basic_configuration_measures_label',
                  'Measures'
                )}
                placeholder={t(
                  'widget_builder_basic_configuration_measures_placeholder',
                  'Select measures to display'
                )}
                data={availableMeasureOptions}
                value={config.measures as string[] || []}
                onChange={( value ) => updateConfig( 'measures', value )}
                className={classes.select}
                checkIconPosition="right"
                searchable
                clearable
                error={sectionErrors?.measures}
                mt="md"
              />
            )}

            <Select
              label={t(
                'widget_builder_basic_configuration_widget_type_label',
                'Widget Type'
              )}
              placeholder={t(
                'widget_builder_basic_configuration_widget_type_placeholder',
                'Select a widget type'
              )}
              /*
                THE HUMAN NAME, not the raw type string (2026-08-17). This offered
                `criticalStock`, `donutChart`, `kpi` — identifiers, in a dropdown a
                seller is expected to choose from, in a Spanish UI. `widgetCatalog`
                has carried the translated label and a one-line description for
                every type since S18; this select simply never asked for them.

                The description rides along as `Select`'s per-option secondary line,
                so the dropdown explains what each card DOES at the moment of
                choosing rather than after the fact.
              */
              data={availableWidgetTypes.map(( widgetType ) => ({
                value: widgetType,
                label: t( widgetLabelKey( widgetType ), widgetLabelFallback( widgetType ))
              }))}
              renderOption={({ option }) => (
                <div>
                  <Text size="sm">{option.label}</Text>
                  <Text size="xs" c="dimmed">
                    {t( widgetDescriptionKey( option.value ), widgetDescriptionFallback( option.value ))}
                  </Text>
                </div>
              )}
              value={localWidgetType}
              onChange={( value ) => handleWidgetTypeChange( value as string )}
              clearable={false}
              allowDeselect={false}
              className={classes.select}
              checkIconPosition="right"
              error={sectionErrors?.type}
              mt="md"
            />
          </Paper>

          {/* Widget Name and Description */}
          <Paper p="md" className={classes.content} mt="md">
            <TextInput
              label={t(
                'widget_builder_basic_configuration_widget_name_label',
                'Widget Name'
              )}
              placeholder={t(
                'widget_builder_basic_configuration_widget_name_placeholder',
                'Enter a name for this widget'
              )}
              value={config.name}
              onChange={( event ) => updateConfig( 'name', event.currentTarget.value )}
              className={classes.input}
              error={sectionErrors?.name}
            />

            <Textarea
              label={t(
                'widget_builder_basic_configuration_widget_description_label',
                'Widget Description (optional)'
              )}
              placeholder={t(
                'widget_builder_basic_configuration_widget_description_placeholder',
                'Enter a description for this widget'
              )}
              value={config.description || ''}
              onChange={( event ) => updateConfig( 'description', event.currentTarget.value )}
              className={classes.input}
              mt="md"
            />

            <TextInput
              label={t(
                'widget_builder_basic_configuration_widget_title_label',
                'Widget Title (optional)'
              )}
              placeholder={t(
                'widget_builder_basic_configuration_widget_title_placeholder',
                'Enter a title to display on the widget'
              )}
              value={config.title || ''}
              onChange={( event ) => updateConfig( 'title', event.currentTarget.value )}
              className={classes.input}
              mt="md"
            />
          </Paper>
        </Box>
      </Stack>
    </div>
  );
}
